import React, { useState, useEffect, useRef } from 'react';
import { X, Loader, Sparkles, RefreshCw } from 'lucide-react';
import { generateFinancialSummary } from '../services/openai';

const STORAGE_KEY = 'financeTracker_openaiKey';

const getSavedKey = () =>
  process.env.REACT_APP_OPENAI_API_KEY || localStorage.getItem(STORAGE_KEY) || '';

// Render GPT markdown: **bold**, ## headers, bullet lines
const renderContent = (text) => {
  const lines = text.split('\n');
  return lines.map((line, i) => {
    // Section header: ## or **Header**
    if (/^#{1,3}\s/.test(line)) {
      const content = line.replace(/^#{1,3}\s/, '');
      return <p key={i} className="text-gray-900 font-semibold text-sm mt-5 mb-1 first:mt-0">{content}</p>;
    }
    if (/^\*\*[^*]+\*\*$/.test(line.trim())) {
      const content = line.trim().replace(/^\*\*|\*\*$/g, '');
      return <p key={i} className="text-gray-900 font-semibold text-sm mt-5 mb-1 first:mt-0">{content}</p>;
    }
    // Bullet point
    if (/^[-•]\s/.test(line.trim())) {
      const content = line.trim().replace(/^[-•]\s/, '');
      return (
        <div key={i} className="flex gap-2 text-sm text-gray-600 leading-relaxed">
          <span className="text-accent mt-0.5 shrink-0">·</span>
          <span dangerouslySetInnerHTML={{ __html: inlineBold(content) }} />
        </div>
      );
    }
    // Numbered list
    if (/^\d+\.\s/.test(line.trim())) {
      const [num, ...rest] = line.trim().split(/\.\s(.+)/);
      return (
        <div key={i} className="flex gap-2 text-sm text-gray-600 leading-relaxed">
          <span className="text-accent shrink-0 w-4">{num}.</span>
          <span dangerouslySetInnerHTML={{ __html: inlineBold(rest.join('')) }} />
        </div>
      );
    }
    // Empty line
    if (!line.trim()) return <div key={i} className="h-2" />;
    // Normal paragraph
    return (
      <p key={i} className="text-sm text-gray-600 leading-relaxed"
        dangerouslySetInnerHTML={{ __html: inlineBold(line) }} />
    );
  });
};

const inlineBold = (text) =>
  text.replace(/\*\*(.+?)\*\*/g, '<span class="text-gray-900 font-medium">$1</span>');

const AISummaryModal = ({ onClose, data }) => {
  const [apiKey, setApiKey] = useState(getSavedKey);
  const [keyDraft, setKeyDraft] = useState('');
  const [summary, setSummary] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const hasFetched = useRef(false);

  const generate = async (key) => {
    const k = key || apiKey;
    if (!k) return;
    setLoading(true);
    setError('');
    setSummary('');
    try {
      const result = await generateFinancialSummary(k, data);
      setSummary(result);
      if (!process.env.REACT_APP_OPENAI_API_KEY) {
        localStorage.setItem(STORAGE_KEY, k);
        setApiKey(k);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Auto-generate if we already have a key
  useEffect(() => {
    if (apiKey && !hasFetched.current) {
      hasFetched.current = true;
      generate(apiKey);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const needsKey = !apiKey && !loading && !summary;

  return (
    <div className="fixed inset-0 bg-gray-900/40 flex items-start justify-center p-4 z-50 overflow-y-auto">
      <div className="bg-white border border-gray-100 rounded-2xl shadow-xl w-full max-w-2xl my-8 overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <Sparkles className="w-3.5 h-3.5 text-accent" />
            <span className="text-gray-900 text-sm font-medium">AI Financial Summary</span>
          </div>
          <div className="flex items-center gap-3">
            {summary && (
              <button
                onClick={() => generate(apiKey)}
                className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-900 transition-colors"
              >
                <RefreshCw className="w-3 h-3" />
                Regenerate
              </button>
            )}
            <button onClick={onClose} className="text-gray-400 hover:text-gray-900 transition-colors">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="p-5">
          {/* No API key */}
          {needsKey && (
            <div className="space-y-4">
              <p className="text-gray-600 text-sm">
                Enter your OpenAI API key to generate a detailed analysis of your financial data.
              </p>
              <div className="space-y-2">
                <input
                  type="password"
                  value={keyDraft}
                  onChange={e => setKeyDraft(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && generate(keyDraft)}
                  placeholder="sk-..."
                  className="w-full bg-gray-50 border border-gray-200 rounded-lg text-gray-900 text-sm px-3 py-2 outline-none focus:border-accent focus:ring-2 focus:ring-emerald-100 placeholder-gray-400"
                  autoFocus
                />
                <p className="text-xs text-gray-400">
                  Stored locally in your browser. Never sent to any server other than OpenAI directly.
                </p>
              </div>
              <button
                onClick={() => generate(keyDraft)}
                disabled={!keyDraft.trim()}
                className="px-4 py-2 bg-accent text-white rounded-lg text-sm font-medium hover:bg-emerald-700 transition-colors disabled:opacity-40"
              >
                Generate Summary
              </button>
            </div>
          )}

          {/* Loading */}
          {loading && (
            <div className="flex items-center gap-3 py-12 justify-center">
              <Loader className="w-4 h-4 animate-spin text-accent" />
              <span className="text-gray-500 text-sm">Analyzing your finances…</span>
            </div>
          )}

          {/* Error */}
          {error && !loading && (
            <div className="space-y-3">
              <p className="text-sm text-negative">{error}</p>
              <div className="flex gap-3">
                <button
                  onClick={() => generate(apiKey)}
                  className="text-sm text-accent hover:text-emerald-700 transition-colors"
                >
                  Try again
                </button>
                <button
                  onClick={() => { setApiKey(''); setError(''); localStorage.removeItem(STORAGE_KEY); }}
                  className="text-sm text-gray-400 hover:text-gray-900 transition-colors"
                >
                  Change API key
                </button>
              </div>
            </div>
          )}

          {/* Summary */}
          {summary && !loading && (
            <div className="space-y-0.5">
              {renderContent(summary)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AISummaryModal;
